import dayjs, { type Dayjs } from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'

import type { FilterOption } from 'features/filters/types'
import { getBadgeLabels } from 'features/filters/utils/helpers'
import type {
  ChartValue,
  FilterCriterion,
  IssueCardData,
  IssueResponse,
  RunSchedulePreset,
} from 'features/trend-detection/types'
import { looksLikeId } from 'features/trend-detection/utils/filter-value-list'

dayjs.extend(relativeTime)

/** Names a stored value the filter options can't — the same shape as
 * `FilterControlProps['getValueLabel']`. */
type ValueLabelResolver = (field: string, value: string) => string | undefined

export const ITEMS_PER_PAGE = 10

const STALE_RUN_THRESHOLD_MS = 60 * 60 * 1000 // 1 hour

export function isRunStale(runCreateTime: string): boolean {
  return Date.now() - new Date(runCreateTime).getTime() > STALE_RUN_THRESHOLD_MS
}

export const DEFAULT_AGENT_CONFIG =
  'Find issues with my products across all my datasets.'

/** Build issue card view models from API issue payloads. post_texts filled by lazy endpoint. */
export function mapIssuesToCardData(issues: IssueResponse[]): IssueCardData[] {
  return issues.map((issue, index) => ({
    ...issue.issue_data,
    post_texts: [],
    original_index: index,
    issue_uuid: issue.uuid,
    is_severe: issue.is_severe ?? issue.issue_data.is_severe,
  }))
}

function formatDateForFilter(val: number | string): string {
  if (val === null || val === undefined) return ''
  const n = typeof val === 'string' ? Number(val) : val
  let date: Date
  if (typeof val === 'string' && isNaN(n)) {
    date = new Date(val)
  } else if (!isNaN(n)) {
    date = new Date(n < 1e12 ? n * 1000 : n)
  } else {
    return ''
  }
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleDateString('en-US', { timeZone: 'UTC' })
}

/**
 * FE-side chip labels for stored criterion field names. Two kinds of entry:
 *   (a) Fields that don't appear in filterOptions at all (parent_theme,
 *       child_theme — emitted by the LLM text-to-filter pipeline; the backend
 *       aliases child_theme → theme).
 *   (b) Fields that exist in filterOptions but where we want the FE label to
 *       override the backend alias — currently just `theme`, which the LLM
 *       may emit for sub-theme criteria and we want labeled "Sub-theme" so
 *       chips don't collide with parent_theme's "Theme" label.
 */
const BACKEND_FIELD_ALIASES: Record<string, string> = {
  parent_theme: 'Parent Theme',
  theme: 'Sub-Theme',
  child_theme: 'Sub-Theme',
  brand: 'Brand',
  product: 'Product',
  product_brand: 'Brand',
  product_hierarchy: 'Product',
  source: 'Source',
  country: 'Country',
  promotional: 'Promotional',
  rating: 'Rating',
  proj_uuid: 'Dataset',
}

/**
 * Resolve a criterion's stored values to chip labels, keeping the ones that
 * resolve to nothing.
 *
 * For theme and proj_uuid, `getBadgeLabels` builds labels by filtering the
 * OPTION list down to the entries the criterion selected. A stored value the
 * project no longer offers matches no entry, so it contributes no label and
 * disappears from the chip and its tooltip — the filter is applied, and the
 * only surface that says so shows a shorter list. Other fields fall through
 * to returning the stored values verbatim, so they never had this problem.
 *
 * Unresolved name-shaped values are appended as themselves. An unresolved
 * cluster uuid is NOT: the options omit live clusters with no counted posts,
 * so from the options alone it could be live or deleted. `getValueLabel` (the
 * agent read's saved-value labels, see `useAgentFilterPanel`) settles it —
 * a live uuid gets its topic, a deleted one `[Deleted Value]`. Without it the
 * uuid still contributes nothing, which is how read-only callers behave.
 */
function resolveCriterionLabels(
  field: string,
  values: string[],
  filterOptions: FilterOption[] | undefined,
  getValueLabel?: ValueLabelResolver,
): string[] {
  const resolved = getBadgeLabels({ field, values }, filterOptions) ?? values
  const option = filterOptions?.find((o) => o.field === field)
  if (!option || !('values' in option)) return resolved

  const offered = new Set<string>()
  for (const item of option.values) {
    offered.add(item.value)
    if (item.cluster_uuid) offered.add(item.cluster_uuid)
  }
  // Same restriction as the picker: an unmatched cluster uuid is not evidence
  // the value is dead, because the options do not enumerate every uuid that
  // carries a topic. Only name-shaped values are judged here.
  const unresolved = values.flatMap((v) => {
    if (offered.has(v)) return []
    if (!looksLikeId(v)) return [v]
    const label = getValueLabel?.(field, v)
    return label ? [label] : []
  })
  return unresolved.length > 0 ? [...resolved, ...unresolved] : resolved
}

/**
 * Format a filter criterion for display in tags/badges.
 * Pass filterOptions to resolve field names to aliases and UUIDs to display names.
 */
export const formatFilterCriterion = (
  criterion: FilterCriterion,
  filterOptions?: FilterOption[],
  getValueLabel?: ValueLabelResolver,
): string => {
  const { field, value, values, min, max } = criterion
  // BACKEND_FIELD_ALIASES wins over filterOptions.alias so the FE has final
  // say over chip labels for fields it explicitly cares about (see the alias
  // map's docstring for rationale).
  const label =
    BACKEND_FIELD_ALIASES[field] ??
    filterOptions?.find((o) => o.field === field)?.alias ??
    field

  if (field === 'create_time' && (min != null || max != null)) {
    const minDate = min != null ? formatDateForFilter(min) : ''
    const maxDate = max != null ? formatDateForFilter(max) : ''
    if (minDate && maxDate) {
      return `${label}: ${minDate} to ${maxDate}`
    }
    if (minDate) return `${label}: after ${minDate}`
    if (maxDate) return `${label}: before ${maxDate}`
    if (min != null && max != null) {
      return `${label}: ${String(min)} to ${String(max)}`
    }
    if (min != null) return `${label}: after ${String(min)}`
    if (max != null) return `${label}: before ${String(max)}`
  }

  // Account has no datasets: filterOptions loaded but no proj_uuid option exists — hide the criterion
  if (
    field.match('proj_uuid') &&
    filterOptions &&
    !filterOptions.some((o) => o.field === field)
  ) {
    return ''
  }

  const filterValues = values || value
  if (filterValues) {
    // Use shared getBadgeLabels to resolve UUIDs → display names when filter options are available
    const resolvedValues =
      filterOptions && Array.isArray(filterValues)
        ? resolveCriterionLabels(
            field,
            filterValues,
            filterOptions,
            getValueLabel,
          )
        : filterValues

    // A single theme can have one cluster_uuid per dataset (e.g. "Price &
    // Value: Cost" appears once in each project's filter_options), so a saved
    // filter with N uuids can resolve to N copies of the same display name.
    // Dedupe to avoid "Price / Value: Pricing" rendering 9 times.
    const uniqueValues = Array.isArray(resolvedValues)
      ? Array.from(new Set(resolvedValues))
      : resolvedValues

    if (Array.isArray(uniqueValues) && uniqueValues.length > 2) {
      return `${label}: ${uniqueValues.slice(0, 2).join(', ')} +${uniqueValues.length - 2} more`
    }
    const displayValue = Array.isArray(uniqueValues)
      ? uniqueValues.join(', ')
      : uniqueValues
    if (!displayValue) return ''
    return `${label}: ${displayValue}`
  }

  return label
}

export function getFilterCriterionAllValues(
  criterion: FilterCriterion,
  filterOptions?: FilterOption[],
  getValueLabel?: ValueLabelResolver,
): string[] | null {
  const { field, value, values } = criterion
  const filterValues = values || value
  if (!Array.isArray(filterValues) || filterValues.length <= 2) return null

  const resolved = filterOptions
    ? resolveCriterionLabels(field, filterValues, filterOptions, getValueLabel)
    : filterValues

  // Dedupe cluster_uuid → name collisions across datasets so the tooltip
  // doesn't list the same theme repeatedly (see formatFilterCriterion).
  const unique = Array.isArray(resolved)
    ? Array.from(new Set(resolved))
    : resolved

  return Array.isArray(unique) && unique.length > 2
    ? (unique as string[])
    : null
}

/**
 * `singleLine` is set for the date-range branch (no discrete values); callers
 * render it as one line instead of a bulleted `values` list.
 */
export const expandCriterion = (
  criterion: FilterCriterion,
  filterOptions?: FilterOption[],
): { fieldLabel: string; values: string[]; singleLine?: string } | null => {
  const { field, value, values, min, max } = criterion
  const fieldLabel =
    BACKEND_FIELD_ALIASES[field] ??
    filterOptions?.find((o) => o.field === field)?.alias ??
    field

  if (field === 'create_time' && (min != null || max != null)) {
    const minDate = min != null ? formatDateForFilter(min) : ''
    const maxDate = max != null ? formatDateForFilter(max) : ''
    let dateStr = ''
    if (minDate && maxDate) dateStr = `${minDate} to ${maxDate}`
    else if (minDate) dateStr = `after ${minDate}`
    else if (maxDate) dateStr = `before ${maxDate}`
    else if (min != null && max != null)
      dateStr = `${String(min)} to ${String(max)}`
    else if (min != null) dateStr = `after ${String(min)}`
    else if (max != null) dateStr = `before ${String(max)}`
    if (dateStr) return { fieldLabel, values: [], singleLine: dateStr }
  }

  if (
    field.match('proj_uuid') &&
    filterOptions &&
    !filterOptions.some((o) => o.field === field)
  ) {
    return null
  }

  const filterValues = values || value
  if (!filterValues) return { fieldLabel, values: [] }

  // resolveCriterionLabels (not getBadgeLabels directly) so name-shaped values
  // with no matching option are appended rather than silently dropped.
  const resolvedValues =
    filterOptions && Array.isArray(filterValues)
      ? resolveCriterionLabels(field, filterValues, filterOptions)
      : filterValues

  const uniqueValues = Array.isArray(resolvedValues)
    ? Array.from(new Set(resolvedValues))
    : resolvedValues

  if (!Array.isArray(uniqueValues)) {
    return uniqueValues ? { fieldLabel, values: [String(uniqueValues)] } : null
  }
  if (uniqueValues.length === 0) return null
  return { fieldLabel, values: uniqueValues }
}

/**
 * Scope-cell variant of `formatFilterCriterion` that skips the "+N more"
 * value-level truncation. Returns "Label: v1, v2, v3" with all values —
 * the caller's container (CSS ellipsis) or tooltip handles overflow.
 *
 * `maxValues`:
 * - `Infinity` (default) → all values, no truncation. Right for cell text
 *   that CSS-ellipses at its container edge.
 * - a finite N → shows first N values then "…and (X-N) more". Right for
 *   tooltip bullets so a criterion with 100 values doesn't wall-of-text.
 */
export const formatCriterionExpanded = (
  criterion: FilterCriterion,
  filterOptions?: FilterOption[],
  maxValues: number = Infinity,
): string => {
  const { field, value, values, min, max } = criterion
  const label =
    BACKEND_FIELD_ALIASES[field] ??
    filterOptions?.find((o) => o.field === field)?.alias ??
    field

  // Date-range branch: identical to `formatFilterCriterion` — no value
  // truncation applies since there's only ever min/max.
  if (field === 'create_time' && (min != null || max != null)) {
    const minDate = min != null ? formatDateForFilter(min) : ''
    const maxDate = max != null ? formatDateForFilter(max) : ''
    if (minDate && maxDate) return `${label}: ${minDate} to ${maxDate}`
    if (minDate) return `${label}: after ${minDate}`
    if (maxDate) return `${label}: before ${maxDate}`
    if (min != null && max != null) {
      return `${label}: ${String(min)} to ${String(max)}`
    }
    if (min != null) return `${label}: after ${String(min)}`
    if (max != null) return `${label}: before ${String(max)}`
  }

  if (
    field.match('proj_uuid') &&
    filterOptions &&
    !filterOptions.some((o) => o.field === field)
  ) {
    return ''
  }

  const filterValues = values || value
  if (!filterValues) return label

  const resolvedValues =
    filterOptions && Array.isArray(filterValues)
      ? (getBadgeLabels({ field, values: filterValues }, filterOptions) ??
        filterValues)
      : filterValues

  const uniqueValues = Array.isArray(resolvedValues)
    ? Array.from(new Set(resolvedValues))
    : resolvedValues

  if (!Array.isArray(uniqueValues)) {
    return uniqueValues ? `${label}: ${uniqueValues}` : ''
  }
  if (uniqueValues.length === 0) return ''

  if (uniqueValues.length <= maxValues) {
    return `${label}: ${uniqueValues.join(', ')}`
  }
  const shown = uniqueValues.slice(0, maxValues).join(', ')
  const remaining = uniqueValues.length - maxValues
  return `${label}: ${shown} …and ${remaining} more`
}

export const getMostRecentSunday = (): Date => {
  const today = new Date()
  const utcYear = today.getUTCFullYear()
  const utcMonth = today.getUTCMonth()
  const utcDate = today.getUTCDate()
  const utcDayOfWeek = today.getUTCDay()

  const daysSinceSunday = utcDayOfWeek === 0 ? 0 : utcDayOfWeek

  const sunday = new Date(
    Date.UTC(utcYear, utcMonth, utcDate - daysSinceSunday, 0, 0, 0, 0),
  )
  return sunday
}

/**
 * Returns UTC date range for a run preset.
 * - daily: previous UTC calendar day (00:00:00.000Z through 23:59:59.999Z)
 * - weekly: previous completed Sunday–Saturday week (Sunday 00:00 through Saturday 23:59:59.999)
 * - custom: empty (caller must provide dates)
 */
export const calculateRunScheduleFromPreset = (
  preset: RunSchedulePreset | 'week', // 'week' is legacy, kept for backward compat
): { min: string; max: string } => {
  const mostRecentSunday = getMostRecentSunday()

  const endSaturday = new Date(mostRecentSunday)
  endSaturday.setUTCDate(endSaturday.getUTCDate() - 1)
  endSaturday.setUTCHours(23, 59, 59, 999)

  switch (preset) {
    case 'daily': {
      const now = new Date()
      const today = new Date(
        Date.UTC(
          now.getUTCFullYear(),
          now.getUTCMonth(),
          now.getUTCDate(),
          0,
          0,
          0,
          0,
        ),
      )
      const yesterday = new Date(today)
      yesterday.setUTCDate(yesterday.getUTCDate() - 1)
      const endYesterday = new Date(yesterday)
      endYesterday.setUTCHours(23, 59, 59, 999)
      return {
        min: yesterday.toISOString(),
        max: endYesterday.toISOString(),
      }
    }
    case 'weekly':
    case 'week': {
      const startSunday = new Date(mostRecentSunday)
      startSunday.setUTCDate(startSunday.getUTCDate() - 7)
      return {
        min: startSunday.toISOString(),
        max: endSaturday.toISOString(),
      }
    }
    case 'custom':
      return { min: '', max: '' }
  }
}

export const customRangeToUtcMidnightISO = (d: Dayjs): string =>
  `${d.format('YYYY-MM-DD')}T00:00:00.000Z`

export const customRangeToUtcEndOfDayISO = (d: Dayjs): string =>
  `${d.format('YYYY-MM-DD')}T23:59:59.999Z`

export const formatUtcDateFromISO = (iso: string, addDays = 0): string => {
  const d = new Date(iso)
  if (addDays) d.setUTCDate(d.getUTCDate() + addDays)
  return d.toLocaleDateString(undefined, { timeZone: 'UTC' })
}

// For create_time fields from the backend: the server sends naive UTC strings
// without a Z suffix. Appending Z ensures JS treats them as UTC, then we
// display in the user's local timezone so "9pm ET on 5/5" shows as 5/5, not 5/6.
export const formatLocalDateFromISO = (iso: string): string => {
  const normalized = /Z|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : `${iso}Z`
  return new Date(normalized).toLocaleDateString()
}

/**
 * Format the original analysis period for display on issue update cards.
 * Single day (or same calendar day): "Mar 2, 2026".
 * Multi-day range: "Feb 23 - Mar 1, 2026".
 * Returns null if either date is missing or invalid.
 */
export function formatOriginalAnalysisPeriod(
  startIso: string | null | undefined,
  endIso: string | null | undefined,
): string | null {
  if (!startIso || !endIso) return null
  const startDate = new Date(startIso)
  const endDate = new Date(endIso)
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime()))
    return null
  const opts: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }
  const startStr = startDate.toLocaleDateString('en-US', opts)
  const endStr = endDate.toLocaleDateString('en-US', opts)
  const startDay = startIso.slice(0, 10)
  const endDay = endIso.slice(0, 10)
  if (startDay === endDay) return startStr
  return `${startStr} - ${endStr}`
}

export const parseUtcDateAsCalendarDay = (iso: string): Dayjs =>
  dayjs(iso.slice(0, 10))

export const stableStringify = (obj: unknown): string => {
  if (obj === null || obj === undefined) {
    return JSON.stringify(obj)
  }

  if (typeof obj !== 'object') {
    return JSON.stringify(obj)
  }

  if (Array.isArray(obj)) {
    const processedArray = obj.map((item: unknown) => {
      if (item && typeof item === 'object' && !Array.isArray(item)) {
        const rec = item as Record<string, unknown>
        return Object.keys(rec)
          .sort()
          .reduce(
            (result, k) => {
              result[k] = rec[k]
              return result
            },
            {} as Record<string, unknown>,
          )
      }
      return item
    })
    return JSON.stringify(processedArray)
  }

  const rec = obj as Record<string, unknown>
  const sortedObj: Record<string, unknown> = {}
  const sortedKeys = Object.keys(rec).sort()

  for (const key of sortedKeys) {
    const value = rec[key]
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      const nested = value as Record<string, unknown>
      sortedObj[key] = Object.keys(nested)
        .sort()
        .reduce(
          (result, k) => {
            result[k] = nested[k]
            return result
          },
          {} as Record<string, unknown>,
        )
    } else if (Array.isArray(value)) {
      sortedObj[key] = value.map((item: unknown) => {
        if (item && typeof item === 'object' && !Array.isArray(item)) {
          const itemRec = item as Record<string, unknown>
          return Object.keys(itemRec)
            .sort()
            .reduce(
              (result, k) => {
                result[k] = itemRec[k]
                return result
              },
              {} as Record<string, unknown>,
            )
        }
        return item
      })
    } else {
      sortedObj[key] = value
    }
  }

  return JSON.stringify(sortedObj)
}

export type EmergenceLevel =
  | 'nothing'
  | 'slightly_elevated'
  | 'elevated'
  | 'sharply_elevated'

export function getEmergenceLevel(
  score: number | undefined | null,
): EmergenceLevel {
  if (score === undefined) return 'nothing'
  if (score === null) return 'sharply_elevated' // null = no prior baseline (entirely new issue)
  if (score >= 5) return 'sharply_elevated'
  if (score >= 2) return 'elevated'
  if (score >= 1) return 'slightly_elevated'
  return 'nothing'
}

export function formatRelativeTime(timestamp: string): string {
  return dayjs(timestamp).fromNow()
}

export const formatDateLabel = (
  isoString: string | number | null,
): string | number | null => {
  if (!isoString || typeof isoString !== 'string') return isoString

  try {
    const date = new Date(isoString)
    if (isNaN(date.getTime())) return isoString

    const month = date.toLocaleString('en-US', {
      month: 'short',
      timeZone: 'UTC',
    })
    const day = date.getUTCDate()
    return `${month} ${day}`
  } catch {
    return isoString
  }
}

const WEEKS_PER_MONTH_BUCKET = 4

/**
 * Aggregate weekly ChartValue items into 4-week buckets for the "month" preset.
 * Each bucket uses the first week's date as the category and sums the values.
 * Backend always returns weekly data; we do not use ClickHouse month buckets
 * because they align to calendar month boundaries, not Sunday-aligned 4-week windows.
 */
export function aggregateWeeksToMonthBuckets(data: ChartValue[]): ChartValue[] {
  if (!data?.length) return data
  const aggregated: ChartValue[] = []
  for (let i = 0; i < data.length; i += WEEKS_PER_MONTH_BUCKET) {
    const chunk = data.slice(i, i + WEEKS_PER_MONTH_BUCKET)
    const first = chunk[0]
    if (!first) continue
    const sum = chunk.reduce((acc, item) => acc + (item.values[0] ?? 0), 0)
    aggregated.push({
      category: [first.category[0] ?? null],
      values: [sum],
    })
  }
  return aggregated
}
